import { Icon } from '@design-system';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import classNames from 'classnames';
import {
  AnimatePresence,
  motion,
  MotionValue,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'motion/react';
import type {
  GetStaticPropsContext,
  GetStaticPropsResult,
  InferGetStaticPropsType,
} from 'next';
import { useRouter } from 'next/router';
import {
  createContext,
  CSSProperties,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { HideHubspot } from '../../../components/HideHubspot';
import { Image } from '../../../components/Image';
import Head from '../../../components/Layout/Head';
import { useTheme } from '../../../components/Layout/Theme/useTheme';
import SocialMetadata from '../../../components/SocialMetadata';
import { toCanonicalUrl } from '../../../helpers/links';
import { imageOgStaticLoader } from '../../../helpers/og-image';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import styles from '../../../styles/moma-ps1-i-love-you.module.css';
import { Franchises, HeroImage } from '../../../types/Franchises';
import {
  getStoryblokPage,
  StoryResponse,
  useStoryblok,
} from '../../../utils/storyblok';
import { pageTranslations } from '../../../utils/translations';

enum TimelineEventEnum {
  HERO = 'hero',
  SECTION_1 = '1970',
  SECTION_2 = '1980',
  SECTION_3 = '1990',
  SECTION_4 = '2000',
  SECTION_5 = '2020',
}

type TimelineEventHeroImageType = {
  depth: number;
  className: string;
  positionClassName: string;
  filename: string;
  alt: string;
};

type TimelineEventSectionImageType = {
  depth: number;
  className: string;
  filename: string;
  alt: string;
};

type TimelineEventHeroType = {
  id: TimelineEventEnum.HERO;
  images: TimelineEventHeroImageType[];
};

type TimelineEventSectionType = {
  id:
    | TimelineEventEnum.SECTION_1
    | TimelineEventEnum.SECTION_2
    | TimelineEventEnum.SECTION_3
    | TimelineEventEnum.SECTION_4
    | TimelineEventEnum.SECTION_5;
  year: string;
  title: string;
  images: TimelineEventSectionImageType[];
};

const POST_ITS = [
  'https://images.avantarte.org/media/3DkhLKms11MQfowwm3ftV8XkHSC',
  'https://images.avantarte.org/media/3DkhLLVpjwK9Y1N2wuFX5UHgc8Y',
  'https://images.avantarte.org/media/3DkhYw8HhvY78cHNrF77NpVyDe7',
  'https://images.avantarte.org/media/3DkhYt7coihE4JmW0FHRRCjmXIN',
  'https://images.avantarte.org/media/3DkhYpzV0ncI8H2gWIMQ3WVMBlg',
  'https://images.avantarte.org/media/3Dkhc1UKxnTb6Oj9EUjAXZnzDvW',
  'https://images.avantarte.org/media/3Dkhc476RmNuGf2rm2Q5F5VpHeZ',
  'https://images.avantarte.org/media/3Dkhc0f1Ti4fAYRwavNrFyhPp1n',
  'https://images.avantarte.org/media/3DkhfXb4fzn5JMRQiBMRTPpaVxI',
  'https://images.avantarte.org/media/3DkhfWWmF5q8brg29PTns4hDzTx',
  'https://images.avantarte.org/media/3DkhfUtiSEaQkMWQGhAZB7CZ90S',
  'https://images.avantarte.org/media/3DkhfWPW6VNtDuOymHTPB6tNMtT',
  'https://images.avantarte.org/media/3DkhfZOlJfVzYm8e1ICrAXkG9aW',
  'https://images.avantarte.org/media/3DkhjU9JyL3anVY4ChuIcfDwDPG',
  'https://images.avantarte.org/media/3DkhjcwmhfACaERsSDnq0nNaJqF',
  'https://images.avantarte.org/media/3DkhjcPgntIRSieZov7pSzTzGuI',
  'https://images.avantarte.org/media/3DkhjZcGBP0SUSedaGCl2qK1crf',
  'https://images.avantarte.org/media/3Dkhjaa3k801yygzlYdLE3NH6kB',
  'https://images.avantarte.org/media/3DkhnzVnjVUm3tx3qq0J5PAOBBY',
  'https://images.avantarte.org/media/3DkhnyKXVbPG013bw0Kx1KBY4OI',
  'https://images.avantarte.org/media/3DkhnwtfiCLUvzIC2Wir0QpQwPf',
  'https://images.avantarte.org/media/3Dkho2Y4lAKjkh2fmHw1dicfJtd',
];

const IMAGES_ALT = {
  // SECTION_1
  'https://images.avantarte.org/media/3DZRcAv22kU8EkgLTzarnH8uQr1':
    'Thomas Struth. P.S.1 (now MoMA PS1). 2225 Jackson Avenue, Long Island City, New York, c. 1975–76. Gelatin silver print.',
  'https://images.avantarte.org/media/3DZSVey3FE0FZitKUJMRdWvbWOs':
    'Richard Nonas "Poster for the P.S. 1 exhibition, "Rooms" (June 9-June 26, 1976)',
  'https://images.avantarte.org/media/3DZSZDlJkKdrRYcLftYbc59283i': '',
  'https://images.avantarte.org/media/3DZSzqNdO5GuBa5ZdKhsJMyY1HM':
    'Harry Shunk. "Frank Gillette working on his installation "Set/Setting" in the exhibition, "Rooms""',
  'https://images.avantarte.org/media/3DZT3ub69HljrYVeCS6rPZNDiOf':
    'View of work by Richard Serra in the exhibition, "Rooms" at MoMA PS1',
  'https://images.avantarte.org/media/3DZT908pSm0WSUzpK9YtuQvIbtM':
    'The October 1976 issue of Artforum, featuring “The Apotheosis of the Crummy Space,” Nancy Foote’s review of the P.S.1 exhibition Rooms',
  'https://images.avantarte.org/media/3DZTJ99hg4wJUkOl4qmYRoQj9wq':
    'Installation view of John Baldessari’s work "Alignment Series: Disaster Story Line (Getting it Straight)" in the exhibition, "Rooms"',
  'https://images.avantarte.org/media/3DZTN3upch4eR90U3ORQkrcGV7U':
    'Installation view of the exhibition Rooms, June 9–26, 1976',
  'https://images.avantarte.org/media/3DZTYWsD530KG4uHIIzO0GPvZ5i':
    'View of Jennifer Bartlett, Drawing and Painting, 1974',
  'https://images.avantarte.org/media/3DZTabKr77GcDm6j1KbbYsQeh4J':
    'View of Judith Shea’s work "King/Queen" in the exhibition, "Rooms" at MoMA PS1',
  // SECTION_2
  'https://images.avantarte.org/media/3DZV11WhVKnJMoFHAyzZRwIu4zs':
    'Lee and his portrait of Debbie Harry at the New York/New Wave exhibition curated by Diego Cortez at P.S.1 Contemporary Art Center, 1981. Photo by Martha Cooper',
  'https://images.avantarte.org/media/3DZV11N9YfLEEWNdQcg5nSEHmAa':
    'Exhibition poster from the History pdf*** need high res file request from MoMA',
  'https://images.avantarte.org/media/3DZZ38RmG2SWY39LvR1ZkHw162u':
    'Jean-Michel Basquiat, Untitled, 1980',
  'https://images.avantarte.org/media/3DZVABeDSOUMQCiRRRKbw3pOXQB':
    'Photograph of video monitor in the P.S. 1 exhibition, "New York/New Wave" [PS1 Exh. #278 [February 15, 1981–April 5, 1981]',
  'https://images.avantarte.org/media/3DZVAJ5aCIZLVhiFrwLktCGxp2C':
    'Installation view of the P.S. 1 exhibition "New York/New Wave" at MoMA PS1',
  'https://images.avantarte.org/media/3DZVHfnqg0eTPh4Ytvhyn7xFytm': '',
  'https://images.avantarte.org/media/3DZVJg8nbp4gExSXsIIKEeHYrLb':
    'Installation view of the P.S. 1 exhibition "New York/New Wave"',
  'https://images.avantarte.org/media/3DZVM0kjkPnC0qnJlyLQdhUfF8O':
    'Installation view of the P.S. 1 exhibition "New York/New Wave"',
  'https://images.avantarte.org/media/3DZVNfd0PseZDXmzS8vlTLpxAEP':
    'Installation view of the P.S. 1 exhibition "New York/New Wave"',
  // SECTION_3
  'https://images.avantarte.org/media/3Da2E48A1EXyWGsATMYhLTTYMlx':
    'MoMA - Gelatin. Percutaneous Delights. 1998. Young Architects Program 1998, MoMA PS1, New York, winner',
  'https://images.avantarte.org/media/3Da2EfVETDrGcf34h4S96YalipR':
    'Gelatin. Percutaneous Delights. 1998. Young Architects Program 1998, MoMA PS1, New York, winner',
  'https://images.avantarte.org/media/3Da2FG1iAoOsAuwjV660jBJoUUy':
    'Percutaneous Delights P.S.1 Contemporary Art Center, New York, USA, Photo © Gelitin',
  'https://images.avantarte.org/media/3Da2FnAv6bjzR7sS3HGgK98urGc':
    'Percutaneous Delights P.S.1 Contemporary Art Center, New York, USA, Photo © Gelitin',
  'https://images.avantarte.org/media/3Da5oAE9vO9k9FwSl0Tmk1pV9cH':
    'Percutaneous Delights P.S.1 Contemporary Art Center, New York, USA, Photo © Gelitin',
  'https://images.avantarte.org/media/3Da2LG0HFTM31fVtjZsneuYJaR3':
    'Gelatin. Percutaneous Delights. 1998. Young Architects Program 1998, MoMA PS1, New York, winner',
  'https://images.avantarte.org/media/3Da2LBfh8O8yNXUq7ZZHIpVBNaL':
    'Percutaneous Delights P.S.1 Contemporary Art Center, New York, USA, Photo © Gelitin',
  'https://images.avantarte.org/media/3Da2L9s7ZwcrhS5c5PhJarX56uI':
    'Percutaneous Delights P.S.1 Contemporary Art Center, New York, USA, Photo © Gelitin',
  'https://images.avantarte.org/media/3Da2LIsTLh4vcTmpcCxM6ILcHJC':
    'Percutaneous Delights P.S.1 Contemporary Art Center, New York, USA, Photo © Gelitin',
  'https://images.avantarte.org/media/3Da2PNidxvTQwCtFfwmOCyPMbuP':
    'Percutaneous Delights P.S.1 Contemporary Art Center, New York, USA, Photo © Gelitin',
  'https://images.avantarte.org/media/3Da2POICTiehZkIs2QlAqC0eV4l':
    'Percutaneous Delights P.S.1 Contemporary Art Center, New York, USA, Photo © Gelitin',
  // SECTION_4
  'https://images.avantarte.org/media/3DcfGKpxH6EtbQAEshAfx480Jg5': '',
  'https://images.avantarte.org/media/3DcexjKJGmHrzTUS4U1Vz7Pq5hK':
    'Mary Beth Edelson, Some Living American Women Artists, 1972',
  'https://images.avantarte.org/media/3DcelF7HgU0cqMKwhBi6ymPdJCs':
    'Marina Abramović, Rhythm 10, 1973',
  'https://images.avantarte.org/media/3Dcel83PWAx4vuUfENDD6Q0JoSf':
    'Untitled Film Still No.3 by Cindy Sherman, 1977',
  'https://images.avantarte.org/media/3Dcf5w3PI1FZpljwoTY4d0pYI7V':
    'Ana Mendieta, Untitled (Glass on Body Imprints), 1972',
  'https://images.avantarte.org/media/3DceS71ILVPpqsdGnVWIkf4gTQu':
    'Louise Bourgeois, A Banquet / A Fashion Show of Body Parts, 1978',
  'https://images.avantarte.org/media/3DceS2n134n7J3Xr8YB4BJXbRlY':
    'Judy Chicago, Childhood Rejection Drawing, from the Rejection Quintet, 1974',
  'https://images.avantarte.org/media/3DcelAhhGV01UsgydAfMeusUO5P':
    'Martha Rosler, Semiotics of the Kitchen, 1975',
  'https://images.avantarte.org/media/3DcexiiKobXV7CTilTpdXoLX9LO':
    'Hannah Wilke, Intercourse with…, 1978',
  'https://images.avantarte.org/media/3DceS8XFRrK4VdLlqUzv7rRlRWb':
    'Yoko Ono, Still image from “FLY” 1970.©Yoko Ono',
  'https://images.avantarte.org/media/3DcexmTbejIcYozf0Vw4aFMnaQS':
    'Carolee Schneemann, Portrait Partials, 1970',
  // SECTION_5
  'https://images.avantarte.org/media/3Dcg96MwWeypXNE4FxZd6giXLUe':
    'Kameron Neal. Down the Barrel (of a Lens) (still). 2023. Two-channel video installation (25:25 min). Courtesy the artist',
  'https://images.avantarte.org/media/3DcgPJXKJBgkh45jACzBMYTQCXf':
    'Nickola Pottinger. Genkle Jesus meek and mild II (detail). 2026. Courtesy the artist and Mrs., Maspeth, NY. Photo: Olympia Shannon',
  'https://images.avantarte.org/media/3DcgYLXWXTaO1IujPomINnf3bXL':
    'Red Canary Song. Lisa’s Flower Spa. 2023. Photograph. Courtesy the artists',
  'https://images.avantarte.org/media/3Dcftq1rhOcrRRbfD7W6spYMOTs':
    'Portrait of the Cevallos Brothers. Courtesy the artists. Photo: Michael Stasiak',
  'https://images.avantarte.org/media/3DcgPOSt4mlq73rQE4jiUThNSza':
    'Piero Penizzotto. Kings of Comedy (Chris, Imani, Bernard, Calvin, D’re). 2024. Papier-mâché, foam and acrylic. 71 × 98 × 54 in. Courtesy the artist and Primary Gallery. Photo: Oriol Tarridas',
  'https://images.avantarte.org/media/3DcftnTVVOoJ7slLvBjewhY0k4q':
    'Women’s History Museum. The Massive Disposal of Experience (still). 2022. Film. Courtesy the artists. Credit: Aidan Barringer',
  'https://images.avantarte.org/media/3DcgYYkELZwr1hyNCGAtqfDna8E':
    'Poyen Wang. Night Stroll (still). 2024–25. HD video with stereo sound (22 min.). Courtesy the artist',
  'https://images.avantarte.org/media/3DcgDemWMPaVhosuZ4Yoyu7oEBH':
    'Louis Osmosis. Variations on Public Affairs & Their Subsequent Invigilators: Morning Dew. 2025 (detail). Metal signage, wire, artificial cobweb, confetti, figurine, steel, ribbon, plywood, and keyboard stand. 60 x 24 x 24 in. Courtesy the artist. Photo: Kunning Huang',
  'https://images.avantarte.org/media/3Dcftpe1RWtPOvnupK8E0D9jDaj':
    'Farah Al Qasimi. Kabob House. 2026. Photograph. 30 × 40 in. Courtesy the artist and François Ghebaly, New York and Los Angeles',
  'https://images.avantarte.org/media/3DcgYSCdcToPBXNmCUyn8WEcudI':
    'Taína Cruz. Charm Written in Steam and Light. 2025. Oil on canvas. 48 × 60 in. Courtesy the artist and Embajada, San Juan. Photo: Luis Corzo',
  'https://images.avantarte.org/media/3Dcg94i1VoKhYZFtDXW8pfysXXt':
    'Julia Wachtel. McSwift. 2024. Oil and acrylic on canvas. 54 × 79 in. Courtesy the artist. Photo: Etienne Frossard',
  'https://images.avantarte.org/media/3DcgPGgXm59Axez0KC4fYxdqbFw':
    'Marc Kokopeli. Times Square Alliance Playset. 2025. LCD cabinet, LED Screen, resin, fabric, mixed media; video, sound (9:15 min). 93 x 154 x 80 cm Courtesy the artist and Galerie Isabella Bortolozzi, Berlin. Photo: Graysc',
};

const SECTION_TITLE = {
  [TimelineEventEnum.SECTION_1]: 'The Birth of PS1',
  [TimelineEventEnum.SECTION_2]: 'The Spirit of Punk!',
  [TimelineEventEnum.SECTION_3]: 'A New Chapter',
  [TimelineEventEnum.SECTION_4]: 'PS1’s Saturn Return',
  [TimelineEventEnum.SECTION_5]: 'PS1 is Your Place',
};

const TIMELINE_EVENT_SECTIONS: (
  | TimelineEventHeroType
  | TimelineEventSectionType
)[] = [
  {
    id: TimelineEventEnum.HERO,
    images: [
      {
        depth: 8,
        className: 'aspect-[1/1] h-[min(25vw,calc(25vh*1.25))]',
        positionClassName: 'translate-x-[15vw] -translate-y-[35vh]',
        filename:
          'https://images.avantarte.org/media/3DZRcAv22kU8EkgLTzarnH8uQr1',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZRcAv22kU8EkgLTzarnH8uQr1'
        ],
      },
      {
        depth: 12,
        className: 'aspect-[1/1] h-[min(40vw,calc(40vh*1.25))]',
        positionClassName: 'translate-x-[45vw] -translate-y-[30vh]',
        filename:
          'https://images.avantarte.org/media/3DZSZDlJkKdrRYcLftYbc59283i',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZSZDlJkKdrRYcLftYbc59283i'
        ],
      },
      {
        depth: 18,
        className: 'aspect-[1/1] h-[min(40vw,calc(40vh*1.25))]',
        positionClassName: 'translate-x-[40vw] translate-y-[20vh]',
        filename:
          'https://images.avantarte.org/media/3Da2E48A1EXyWGsATMYhLTTYMlx',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Da2E48A1EXyWGsATMYhLTTYMlx'
        ],
      },
      {
        depth: 6,
        className: 'aspect-[1/1] h-[min(40vw,calc(40vh*1.25))]',
        positionClassName: '-translate-x-[35vw] -translate-y-[40vh]',
        filename:
          'https://images.avantarte.org/media/3DcfGKpxH6EtbQAEshAfx480Jg5',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DcfGKpxH6EtbQAEshAfx480Jg5'
        ],
      },
      {
        depth: 24,
        className: 'aspect-[1/1] h-[min(30vw,calc(30vh*1.25))]',
        positionClassName: '-translate-x-[15vw] translate-y-[50vh]',
        filename:
          'https://images.avantarte.org/media/3DZVAJ5aCIZLVhiFrwLktCGxp2C',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZVAJ5aCIZLVhiFrwLktCGxp2C'
        ],
      },
      {
        depth: 10,
        className: 'aspect-[1/1] h-[min(30vw,calc(30vh*1.25))]',
        positionClassName: '-translate-x-[10vw] -translate-y-[45vh]',
        filename:
          'https://images.avantarte.org/media/3Da2FnAv6bjzR7sS3HGgK98urGc',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Da2FnAv6bjzR7sS3HGgK98urGc'
        ],
      },
      {
        depth: 16,
        className: 'aspect-[1/1] h-[min(20vw,calc(20vh*1.25))]',
        positionClassName: 'translate-x-[20vw] translate-y-[35vh]',
        filename:
          'https://images.avantarte.org/media/3DZVJg8nbp4gExSXsIIKEeHYrLb',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZVJg8nbp4gExSXsIIKEeHYrLb'
        ],
      },
      {
        depth: 20,
        className: 'aspect-[1/1] h-[min(35vw,calc(35vh*1.25))]',
        positionClassName: '-translate-x-[30vw] translate-y-[30vh]',
        filename:
          'https://images.avantarte.org/media/3Dcg96MwWeypXNE4FxZd6giXLUe',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Dcg96MwWeypXNE4FxZd6giXLUe'
        ],
      },
      {
        depth: 14,
        className: 'aspect-[1/1] h-[min(20vw,calc(20vh*1.25))]',
        positionClassName: 'translate-x-[30vw] translate-y-[21vh]',
        filename:
          'https://images.avantarte.org/media/3Da2L9s7ZwcrhS5c5PhJarX56uI',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Da2L9s7ZwcrhS5c5PhJarX56uI'
        ],
      },
      {
        depth: 20,
        className: 'aspect-[1/1] h-[min(20vw,calc(20vh*1.25))]',
        positionClassName: '-translate-x-[35vw] -translate-y-[0vh]',
        filename:
          'https://images.avantarte.org/media/3DcgYLXWXTaO1IujPomINnf3bXL',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DcgYLXWXTaO1IujPomINnf3bXL'
        ],
      },
    ],
  },
  {
    id: TimelineEventEnum.SECTION_1,
    year: TimelineEventEnum.SECTION_1,
    title: SECTION_TITLE[TimelineEventEnum.SECTION_1],
    images: [
      {
        depth: 12,
        filename:
          'https://images.avantarte.org/media/3DZRcAv22kU8EkgLTzarnH8uQr1',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZRcAv22kU8EkgLTzarnH8uQr1'
        ],
        className: 'col-span-4 col-start-8 row-span-4 row-start-2',
      },
      {
        depth: 24,
        filename:
          'https://images.avantarte.org/media/3DZSVey3FE0FZitKUJMRdWvbWOs',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZSVey3FE0FZitKUJMRdWvbWOs'
        ],
        className: 'col-span-3 col-start-2 row-span-4 row-start-1',
      },
      {
        depth: 40,
        filename:
          'https://images.avantarte.org/media/3DZSZDlJkKdrRYcLftYbc59283i',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZSZDlJkKdrRYcLftYbc59283i'
        ],
        className: 'col-span-5 col-start-3 row-span-8 row-start-5',
      },
      {
        depth: 36,
        filename:
          'https://images.avantarte.org/media/3DZSzqNdO5GuBa5ZdKhsJMyY1HM',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZSzqNdO5GuBa5ZdKhsJMyY1HM'
        ],
        className: 'col-span-4 col-start-1 row-span-4 row-start-4',
      },
      {
        depth: 29,
        filename:
          'https://images.avantarte.org/media/3DZT3ub69HljrYVeCS6rPZNDiOf',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZT3ub69HljrYVeCS6rPZNDiOf'
        ],
        className: 'col-span-4 col-start-9 row-span-4 row-start-6',
      },
      {
        depth: 30,
        filename:
          'https://images.avantarte.org/media/3DZT908pSm0WSUzpK9YtuQvIbtM',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZT908pSm0WSUzpK9YtuQvIbtM'
        ],
        className: 'col-span-4 col-start-4 row-span-4 row-start-2',
      },
      {
        depth: 27,
        filename:
          'https://images.avantarte.org/media/3DZTN3upch4eR90U3ORQkrcGV7U',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZTN3upch4eR90U3ORQkrcGV7U'
        ],
        className: 'col-span-4 col-start-7 row-span-6 row-start-6',
      },
      {
        depth: 6,
        filename:
          'https://images.avantarte.org/media/3DZTYWsD530KG4uHIIzO0GPvZ5i',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZTYWsD530KG4uHIIzO0GPvZ5i'
        ],
        className: 'col-span-3 col-start-2 row-span-4 row-start-8',
      },
      {
        depth: 21,
        filename:
          'https://images.avantarte.org/media/3DZTabKr77GcDm6j1KbbYsQeh4J',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZTabKr77GcDm6j1KbbYsQeh4J'
        ],
        className: 'col-span-4 col-start-6 row-span-3 row-start-1',
      },
    ],
  },
  {
    id: TimelineEventEnum.SECTION_2,
    year: TimelineEventEnum.SECTION_2,
    title: SECTION_TITLE[TimelineEventEnum.SECTION_2],
    images: [
      {
        depth: 12,
        filename:
          'https://images.avantarte.org/media/3DZV11WhVKnJMoFHAyzZRwIu4zs',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZV11WhVKnJMoFHAyzZRwIu4zs'
        ],
        className: 'col-span-4 col-start-2 row-span-4 row-start-2',
      },
      {
        depth: 24,
        filename:
          'https://images.avantarte.org/media/3DZV11N9YfLEEWNdQcg5nSEHmAa',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZV11N9YfLEEWNdQcg5nSEHmAa'
        ],
        className: 'col-span-3 col-start-7 row-span-4 row-start-1',
      },
      {
        depth: 18,
        filename:
          'https://images.avantarte.org/media/3DZZ38RmG2SWY39LvR1ZkHw162u',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZZ38RmG2SWY39LvR1ZkHw162u'
        ],
        className: 'col-span-5 col-start-4 row-span-5 row-start-4',
      },
      {
        depth: 36,
        filename:
          'https://images.avantarte.org/media/3DZVABeDSOUMQCiRRRKbw3pOXQB',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZVABeDSOUMQCiRRRKbw3pOXQB'
        ],
        className: 'col-span-4 col-start-9 row-span-4 row-start-3',
      },
      {
        depth: 8,
        filename:
          'https://images.avantarte.org/media/3DZVAJ5aCIZLVhiFrwLktCGxp2C',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZVAJ5aCIZLVhiFrwLktCGxp2C'
        ],
        className: 'col-span-3 col-start-1 row-span-3 row-start-6',
      },
      {
        depth: 30,
        filename:
          'https://images.avantarte.org/media/3DZVHfnqg0eTPh4Ytvhyn7xFytm',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZVHfnqg0eTPh4Ytvhyn7xFytm'
        ],
        className: 'col-span-4 col-start-8 row-span-4 row-start-7',
      },
      {
        depth: 15,
        filename:
          'https://images.avantarte.org/media/3DZVJg8nbp4gExSXsIIKEeHYrLb',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZVJg8nbp4gExSXsIIKEeHYrLb'
        ],
        className: 'col-span-3 col-start-2 row-span-3 row-start-9',
      },
      {
        depth: 27,
        filename:
          'https://images.avantarte.org/media/3DZVM0kjkPnC0qnJlyLQdhUfF8O',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZVM0kjkPnC0qnJlyLQdhUfF8O'
        ],
        className: 'col-span-5 col-start-5 row-span-4 row-start-9',
      },
      {
        depth: 6,
        filename:
          'https://images.avantarte.org/media/3DZVNfd0PseZDXmzS8vlTLpxAEP',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DZVNfd0PseZDXmzS8vlTLpxAEP'
        ],
        className: 'col-span-2 col-start-11 row-span-2 row-start-11',
      },
    ],
  },
  {
    id: TimelineEventEnum.SECTION_3,
    year: TimelineEventEnum.SECTION_3,
    title: SECTION_TITLE[TimelineEventEnum.SECTION_3],
    images: [
      {
        depth: 42,
        filename:
          'https://images.avantarte.org/media/3Da2E48A1EXyWGsATMYhLTTYMlx',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Da2E48A1EXyWGsATMYhLTTYMlx'
        ],
        className: 'col-span-7 col-start-6 row-span-5 row-start-6',
      },
      {
        depth: 24,
        filename:
          'https://images.avantarte.org/media/3Da2EfVETDrGcf34h4S96YalipR',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Da2EfVETDrGcf34h4S96YalipR'
        ],
        className: 'col-span-3 col-start-1 row-span-2 row-start-2',
      },
      {
        depth: 18,
        filename:
          'https://images.avantarte.org/media/3Da2FG1iAoOsAuwjV660jBJoUUy',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Da2FG1iAoOsAuwjV660jBJoUUy'
        ],
        className: 'col-span-5 col-start-4 row-span-5 row-start-1',
      },
      {
        depth: 36,
        filename:
          'https://images.avantarte.org/media/3Da2FnAv6bjzR7sS3HGgK98urGc',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Da2FnAv6bjzR7sS3HGgK98urGc'
        ],
        className: 'col-span-5 col-start-1 row-span-4 row-start-8',
      },
      {
        depth: 8,
        filename:
          'https://images.avantarte.org/media/3Da5oAE9vO9k9FwSl0Tmk1pV9cH',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Da5oAE9vO9k9FwSl0Tmk1pV9cH'
        ],
        className: 'col-span-3 col-start-10 row-span-4 row-start-1',
      },
      {
        depth: 30,
        filename:
          'https://images.avantarte.org/media/3Da2LG0HFTM31fVtjZsneuYJaR3',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Da2LG0HFTM31fVtjZsneuYJaR3'
        ],
        className: 'col-span-4 col-start-8 row-span-4 row-start-4',
      },
      {
        depth: 15,
        filename:
          'https://images.avantarte.org/media/3Da2LBfh8O8yNXUq7ZZHIpVBNaL',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Da2LBfh8O8yNXUq7ZZHIpVBNaL'
        ],
        className: 'col-span-3 col-start-5 row-span-3 row-start-5',
      },
      {
        depth: 27,
        filename:
          'https://images.avantarte.org/media/3Da2L9s7ZwcrhS5c5PhJarX56uI',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Da2L9s7ZwcrhS5c5PhJarX56uI'
        ],
        className: 'col-span-4 col-start-3 row-span-4 row-start-4',
      },
      {
        depth: 21,
        filename:
          'https://images.avantarte.org/media/3Da2PNidxvTQwCtFfwmOCyPMbuP',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Da2PNidxvTQwCtFfwmOCyPMbuP'
        ],
        className: 'col-span-4 col-start-6 row-span-3 row-start-10',
      },
    ],
  },
  {
    id: TimelineEventEnum.SECTION_4,
    year: TimelineEventEnum.SECTION_4,
    title: SECTION_TITLE[TimelineEventEnum.SECTION_4],
    images: [
      {
        depth: 17,
        filename:
          'https://images.avantarte.org/media/3DcfGKpxH6EtbQAEshAfx480Jg5',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DcfGKpxH6EtbQAEshAfx480Jg5'
        ],
        className: 'col-span-6 col-start-1 row-span-7 row-start-2',
      },
      {
        depth: 39,
        filename:
          'https://images.avantarte.org/media/3DcexjKJGmHrzTUS4U1Vz7Pq5hK',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DcexjKJGmHrzTUS4U1Vz7Pq5hK'
        ],
        className: 'col-span-4 col-start-6 row-span-3 row-start-1',
      },
      {
        depth: 47,
        filename:
          'https://images.avantarte.org/media/3DcelF7HgU0cqMKwhBi6ymPdJCs',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DcelF7HgU0cqMKwhBi6ymPdJCs'
        ],
        className: 'col-span-3 col-start-7 row-span-5 row-start-4',
      },
      {
        depth: 30,
        filename:
          'https://images.avantarte.org/media/3Dcel83PWAx4vuUfENDD6Q0JoSf',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Dcel83PWAx4vuUfENDD6Q0JoSf'
        ],
        className: 'col-span-5 col-start-3 row-span-4 row-start-8',
      },
      {
        depth: 45,
        filename:
          'https://images.avantarte.org/media/3Dcf5w3PI1FZpljwoTY4d0pYI7V',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Dcf5w3PI1FZpljwoTY4d0pYI7V'
        ],
        className: 'col-span-4 col-start-9 row-span-3 row-start-2',
      },
      {
        depth: 7,
        filename:
          'https://images.avantarte.org/media/3DceS71ILVPpqsdGnVWIkf4gTQu',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DceS71ILVPpqsdGnVWIkf4gTQu'
        ],
        className: 'col-span-5 col-start-7 row-span-4 row-start-6',
      },
      {
        depth: 52,
        filename:
          'https://images.avantarte.org/media/3DceS2n134n7J3Xr8YB4BJXbRlY',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DceS2n134n7J3Xr8YB4BJXbRlY'
        ],
        className: 'col-span-3 col-start-2 row-span-4 row-start-9',
      },
      {
        depth: 52,
        filename:
          'https://images.avantarte.org/media/3DcexiiKobXV7CTilTpdXoLX9LO',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DcexiiKobXV7CTilTpdXoLX9LO'
        ],
        className: 'col-span-4 col-start-8 row-span-4 row-start-9',
      },
    ],
  },
  {
    id: TimelineEventEnum.SECTION_5,
    year: TimelineEventEnum.SECTION_5,
    title: SECTION_TITLE[TimelineEventEnum.SECTION_5],
    images: [
      {
        depth: 17,
        filename:
          'https://images.avantarte.org/media/3Dcg96MwWeypXNE4FxZd6giXLUe',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Dcg96MwWeypXNE4FxZd6giXLUe'
        ],
        className: 'col-span-7 col-start-2 row-span-7 row-start-2',
      },
      {
        depth: 24,
        filename:
          'https://images.avantarte.org/media/3DcgPJXKJBgkh45jACzBMYTQCXf',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DcgPJXKJBgkh45jACzBMYTQCXf'
        ],
        className: 'col-span-3 col-start-1 row-span-3 row-start-7',
      },
      {
        depth: 47,
        filename:
          'https://images.avantarte.org/media/3DcgYLXWXTaO1IujPomINnf3bXL',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DcgYLXWXTaO1IujPomINnf3bXL'
        ],
        className: 'col-span-3 col-start-7 row-span-5 row-start-1',
      },
      {
        depth: 30,
        filename:
          'https://images.avantarte.org/media/3Dcftq1rhOcrRRbfD7W6spYMOTs',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3Dcftq1rhOcrRRbfD7W6spYMOTs'
        ],
        className: 'col-span-3 col-start-5 row-span-4 row-start-8',
      },
      {
        depth: 60,
        filename:
          'https://images.avantarte.org/media/3DcgPOSt4mlq73rQE4jiUThNSza',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DcgPOSt4mlq73rQE4jiUThNSza'
        ],
        className: 'col-span-4 col-start-9 row-span-3 row-start-2',
      },
      {
        depth: 24,
        filename:
          'https://images.avantarte.org/media/3DcftnTVVOoJ7slLvBjewhY0k4q',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DcftnTVVOoJ7slLvBjewhY0k4q'
        ],
        className: 'col-span-5 col-start-7 row-span-4 row-start-6',
      },
      {
        depth: 52,
        filename:
          'https://images.avantarte.org/media/3DcgYYkELZwr1hyNCGAtqfDna8E',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DcgYYkELZwr1hyNCGAtqfDna8E'
        ],
        className: 'col-span-4 col-start-2 row-span-3 row-start-10',
      },
      {
        depth: 52,
        filename:
          'https://images.avantarte.org/media/3DcgDemWMPaVhosuZ4Yoyu7oEBH',
        alt: IMAGES_ALT[
          'https://images.avantarte.org/media/3DcgDemWMPaVhosuZ4Yoyu7oEBH'
        ],
        className: 'col-span-3 col-start-9 row-span-4 row-start-9',
      },
    ],
  },
];

const TIMELINE_EVENT_IDS = TIMELINE_EVENT_SECTIONS.map((e) => e.id);

const DEFAULT_SPACING = 24;

const sectionAnimations = {
  initial: (direction: number) => ({ opacity: 0, y: direction * 48 }),
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  },
  exit: (direction: number) => ({
    opacity: 0,
    y: direction * -48,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 1, 1] as [number, number, number, number],
    },
  }),
};

// ----- SlideHeroGrid ----- //

const SlideHeroGridImages = ({
  alt,
  className,
  depth,
  filename,
  mouseX,
  mouseY,
  positionClassName,
}: {
  className: string;
  positionClassName: string;
  filename: string;
  alt: string;
  depth: number;
  mouseX: MotionValue<number>;
  mouseY: MotionValue<number>;
}) => {
  const x = useTransform(mouseX, (v) => v * depth);
  const y = useTransform(mouseY, (v) => v * depth);
  return (
    <div
      style={{ zIndex: depth, position: 'absolute' }}
      className={classNames(positionClassName)}
    >
      <motion.div
        style={{ x, y, display: 'block', position: 'relative' }}
        className={classNames(className)}
      >
        {filename ? (
          <Image
            alt={alt}
            size={{
              sm: { ratio: 'intrinsic', span: 12 },
              md: { ratio: 'intrinsic', span: 4 },
              lg: { ratio: 'intrinsic', span: 4 },
            }}
            ignoreAspectRatio
            src={filename}
          />
        ) : null}
      </motion.div>
    </div>
  );
};

const SlideHeroGrid = ({
  className,
  images,
}: {
  className?: string;
  images: TimelineEventHeroImageType[];
}) => {
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const mouseX = useSpring(rawX, { stiffness: 70, damping: 18 });
  const mouseY = useSpring(rawY, { stiffness: 70, damping: 18 });

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      rawX.set(e.clientX / window.innerWidth - 0.5);
      rawY.set(e.clientY / window.innerHeight - 0.5);
    };
    window.addEventListener('mousemove', handle);
    return () => window.removeEventListener('mousemove', handle);
  }, [rawX, rawY]);

  return (
    <>
      {images.map((image, i) => (
        <SlideHeroGridImages
          key={i}
          positionClassName={classNames(image.positionClassName, className)}
          className={image.className}
          depth={image.depth}
          filename={image.filename}
          alt={image.alt}
          mouseX={mouseX}
          mouseY={mouseY}
        />
      ))}
    </>
  );
};

// ----- SlideSectionGrid ----- //

const SlideSectionGridImage = ({
  alt,
  className,
  depth,
  filename,
  mouseX,
  mouseY,
}: {
  mouseX: MotionValue<number>;
  mouseY: MotionValue<number>;
  depth: number;
  alt: string;
  filename: string;
  className: string;
}) => {
  const x = useTransform(mouseX, (v) => v * depth);
  const y = useTransform(mouseY, (v) => v * depth);
  return (
    <motion.div
      style={{ x, y, zIndex: depth, display: 'block', position: 'relative' }}
      className={classNames(className)}
    >
      {filename ? (
        <Image
          alt={alt}
          className="h-full w-full object-cover"
          size={{
            sm: { ratio: 'intrinsic', span: 12 },
            md: { ratio: 'intrinsic', span: 4 },
            lg: { ratio: 'intrinsic', span: 4 },
          }}
          ignoreAspectRatio
          src={filename}
        />
      ) : null}
    </motion.div>
  );
};

const SlideSectionGrid = ({
  className,
  images,
}: {
  className?: string;
  images: TimelineEventSectionImageType[];
}) => {
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const mouseX = useSpring(rawX, { stiffness: 70, damping: 18 });
  const mouseY = useSpring(rawY, { stiffness: 70, damping: 18 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      rawX.set(e.clientX / window.innerWidth - 0.5);
      rawY.set(e.clientY / window.innerHeight - 0.5);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [rawX, rawY]);

  return (
    <span
      className={classNames(
        'relative grid aspect-[1/1] w-full grid-cols-12 grid-rows-12',
        className,
      )}
    >
      {images.map((image, i) => (
        <SlideSectionGridImage
          key={i}
          mouseX={mouseX}
          mouseY={mouseY}
          depth={image.depth}
          className={image.className}
          alt={image.alt}
          filename={image.filename}
        />
      ))}
    </span>
  );
};

const SlideSection = ({
  item,
  onClick,
}: {
  item: TimelineEventSectionType;
  onClick: () => void;
}) => {
  return (
    <button
      aria-label={`Go to ${item.title}`}
      onClick={onClick}
      className="group flex w-full max-w-2xl flex-col items-center justify-center"
    >
      <SlideSectionGrid
        images={item.images}
        className="relaitve z-0 mb-[var(--spacing)] max-w-[calc(var(--spacing)*10)] transition duration-300 ease-out group-hover:scale-110 md:max-w-[calc(var(--spacing)*25)]"
      />
      <span className="sticky bottom-0 z-10 flex w-full max-w-2xl flex-col bg-gradient-to-t from-white py-[calc(var(--spacing)*2)] transition duration-300 ease-out group-hover:text-black/50">
        <span className="mx-auto w-full max-w-md text-balance text-center">
          {item.year}
        </span>
        <span className="mx-auto w-full max-w-md text-balance text-center text-xl">
          {item.title}
        </span>
        <motion.span
          key="exploreButton"
          className={classNames(
            styles.UiButton,
            'mx-auto mt-[var(--spacing)] h-[calc(var(--spacing)*3)] px-8',
          )}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '0px 0px -24px 0px' }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
        >
          Explore
        </motion.span>
      </span>
    </button>
  );
};

// ----- Hero ----- //

const PageTitle = () => {
  return (
    <motion.h1
      key="pageTitlte"
      className="fixed inset-0 z-70 flex w-full flex-col items-center justify-center p-[calc(var(--spacing)*3)] text-black"
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{
        opacity: 1,
        scale: 1,
        transition: { duration: 1.0, ease: [0.22, 1, 0.36, 1] },
      }}
      exit={{
        opacity: 0,
        scale: 1.03,
        transition: { duration: 0.75, ease: [0.4, 0, 1, 1] },
      }}
    >
      <svg
        className="w-auto"
        viewBox="0 0 864 864"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-labelledby="hero-title"
      >
        <title id="hero-title">PS1 I Love You</title>
        <path
          d="M864 864H0V0H864V864ZM426.638 536.4C371.198 536.4 344.798 572.64 344.798 623.28C344.798 676.08 375.998 711.36 426.638 711.36C477.517 711.36 508.718 676.08 508.718 623.28C508.718 572.64 482.318 536.4 426.638 536.4ZM518.45 540C519.41 565.92 519.65 581.28 519.65 593.76V648.24C519.65 689.76 543.651 711.36 589.011 711.36C634.131 711.36 658.131 689.76 658.131 648.24V593.76C658.131 581.28 658.371 565.92 659.331 540H617.331C618.291 565.92 618.53 581.28 618.53 593.76V649.44C618.53 668.88 607.251 678.72 589.011 678.72C570.531 678.72 559.251 668.88 559.251 649.44V593.76C559.251 581.28 559.49 565.92 560.45 540H518.45ZM192.595 540L255.475 647.04V655.2C255.475 679.2 254.754 697.92 254.274 708H296.274C296.034 697.92 295.074 679.2 295.074 655.2V647.04L358.194 540H314.754L291.475 583.44C284.515 597.12 279.474 607.2 275.874 615.84H275.154C271.794 607.2 266.994 597.12 259.794 583.44L236.515 540H192.595ZM426.638 569.76C455.198 569.76 468.397 590.4 468.397 623.28C468.397 657.36 453.518 679.68 426.638 679.68C399.998 679.68 385.117 657.36 385.117 623.28C385.117 590.4 398.318 569.76 426.638 569.76ZM397.289 344.4C341.849 344.4 315.449 380.64 315.449 431.28C315.449 484.08 346.649 519.36 397.289 519.36C448.169 519.36 479.369 484.08 479.369 431.28C479.369 380.64 452.969 344.4 397.289 344.4ZM104.493 348C104.973 360.48 105.692 380.88 105.692 401.76V462.24C105.692 483.12 104.973 503.52 104.493 516H146.493C146.013 503.52 145.293 483.12 145.293 462.24V401.76C145.293 380.88 146.013 360.48 146.493 348H104.493ZM203.661 348C204.141 360.48 204.861 380.88 204.861 401.76V462.24C204.861 483.12 204.141 503.52 203.661 516H320.542V481.2L244.701 481.68C244.461 475.44 244.462 468.96 244.462 462.24V401.76C244.462 380.88 245.181 360.48 245.661 348H203.661ZM471.237 348C476.517 361.44 484.918 383.04 492.598 404.64L507.717 447.12C517.557 474.48 528.357 502.8 532.677 516H579.717C584.037 502.8 594.837 474.48 604.677 447.12L619.797 404.64C627.477 383.04 635.877 361.44 641.157 348H596.518C593.158 360.48 588.118 376.32 583.558 391.68L569.157 440.16C564.117 457.2 559.077 474.48 557.157 482.4H556.438C554.518 474.48 549.477 457.2 544.438 440.16L530.037 391.68C525.477 376.8 521.157 361.92 517.077 348H471.237ZM642.806 348C643.286 360.48 644.005 380.88 644.005 401.76V462.24C644.005 483.12 643.286 503.52 642.806 516H765.445V482.64L683.846 483.12C683.606 476.4 683.605 469.44 683.605 462.24V447.6H755.846V415.44H683.605V401.76C683.605 394.56 683.606 387.6 683.846 380.88L764.245 381.36V348H642.806ZM397.289 377.76C425.849 377.76 439.049 398.4 439.049 431.28C439.049 465.36 424.169 487.68 397.289 487.68C370.649 487.68 355.77 465.36 355.77 431.28C355.77 398.4 368.969 377.76 397.289 377.76ZM454.833 152.4C414.273 152.4 388.833 172.56 388.833 203.76C388.833 229.68 404.913 246 433.953 252.24L461.793 258C476.673 261.12 484.113 267.84 484.113 277.68C484.113 289.44 473.793 296.4 456.993 296.4C435.633 296.4 422.433 285.12 422.433 267.36H385.473C384.753 303.84 412.113 327.36 455.553 327.36C496.353 327.36 522.753 306.96 522.753 274.8C522.753 248.88 506.673 232.08 474.993 225.36L450.753 220.32C434.433 216.96 427.473 211.92 427.473 201.36C427.473 190.32 437.313 183.36 453.633 183.36C471.633 183.36 482.912 192.48 483.152 207.84H520.113C521.073 173.52 496.113 152.4 454.833 152.4ZM252.814 156C253.294 168.48 254.015 188.88 254.015 209.76V270.24C254.015 291.12 253.294 311.52 252.814 324H294.814C294.334 311.52 293.614 291.12 293.614 270.24V263.52H328.895C360.814 263.52 385.055 242.4 385.055 210.24C385.055 176.16 363.694 156 331.774 156H252.814ZM572.767 156C565.087 172.8 546.606 184.08 524.286 186.24V220.8C542.046 218.88 557.167 211.92 563.887 202.8H565.087V270.24C565.087 283.68 564.607 300.48 563.887 324H604.207C603.487 300.48 603.007 283.68 603.007 270.24V210.72C603.007 192.96 603.727 175.2 604.207 156H572.767ZM322.895 187.2C338.735 187.2 346.175 194.88 346.175 210.24C346.175 224.64 337.294 232.8 322.654 232.8H293.614V209.76C293.614 202.08 293.615 194.4 293.854 187.2H322.895Z"
          fill="currentColor"
        />
      </svg>
    </motion.h1>
  );
};

// ----- Hero ----- //

const Hero = forwardRef<
  HTMLDivElement,
  {
    direction: number;
    navigate: (target: TimelineEventEnum) => void;
    startSection?: TimelineEventEnum;
  }
>(({ direction, navigate, startSection }, ref) => {
  const splideRef = useRef<InstanceType<typeof Splide>>(null);
  const [showTitle, setShowTitle] = useState(true);

  const initialSlide = startSection
    ? TIMELINE_EVENT_SECTIONS.findIndex((item) => item.id === startSection)
    : 0;
  const [currentIndex, setCurrentIndex] = useState(initialSlide);

  useEffect(() => {
    const t = setTimeout(() => setShowTitle(false), 900);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (showTitle) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') splideRef.current?.splide?.go('<');
      if (e.key === 'ArrowRight') splideRef.current?.splide?.go('>');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showTitle]);

  return (
    <motion.div
      ref={ref}
      key="hero"
      animate="animate"
      custom={direction}
      exit="exit"
      initial="initial"
      variants={sectionAnimations}
      className="fixed inset-0 flex h-dvh w-full flex-col"
    >
      <AnimatePresence>
        {showTitle && <PageTitle key="pageTitle" />}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 1 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <Splide
          ref={splideRef}
          hasTrack={false}
          options={{
            rewind: false,
            pagination: false,
            mediaQuery: 'min',

            arrows: true,
            focus: 'center',

            height: '100dvh',
            start: initialSlide,
          }}
          className="relative"
          onMove={(_splide, newIndex) => {
            setCurrentIndex(newIndex);
          }}
        >
          <motion.div
            className={classNames('splide__arrows', styles.UiSplide__arrows)}
            initial={{ opacity: 0 }}
            animate={{ opacity: showTitle ? 0 : 1 }}
            transition={{
              duration: 0.5,
              ease: 'easeOut',
              delay: showTitle ? 0 : 0.4,
            }}
          >
            <motion.button
              aria-label="Previous slide"
              className={classNames(
                'splide__arrow splide__arrow--prev',
                styles.UiSplide__arrows__button,
                styles.UiButton,
              )}
              type="button"
              initial={{ x: -120 }}
              animate={{ x: showTitle ? -120 : 0 }}
              transition={
                showTitle
                  ? { duration: 0.3 }
                  : { type: 'spring', stiffness: 280, damping: 26, delay: 0.35 }
              }
            >
              <Icon
                name="ic_arrow_left"
                className={classNames(styles.UiButton__icon)}
              />
            </motion.button>
            <motion.button
              aria-label="Next slide"
              className={classNames(
                'splide__arrow splide__arrow--next',
                styles.UiSplide__arrows__button,
                styles.UiButton,
              )}
              type="button"
              initial={{ x: 120 }}
              animate={{ x: showTitle ? 120 : 0 }}
              transition={
                showTitle
                  ? { duration: 0.3 }
                  : { type: 'spring', stiffness: 280, damping: 26, delay: 0.45 }
              }
            >
              <Icon
                name="ic_arrow_right"
                className={classNames(styles.UiButton__icon)}
              />
            </motion.button>
          </motion.div>

          <SplideTrack className={classNames(styles.UiSplide__track)}>
            {TIMELINE_EVENT_SECTIONS.map((item) => (
              <SplideSlide
                key={item.id}
                className={classNames(styles.UiSplide__track__slide, {
                  'z-10': item.id === TimelineEventEnum.HERO,
                })}
              >
                {item.id === TimelineEventEnum.HERO ? (
                  <>
                    <SlideHeroGrid
                      images={item.images}
                      className={classNames(
                        'z-10 transition duration-300 ease-out',
                        {
                          'opacity-0': currentIndex !== 0,
                        },
                      )}
                    />

                    <motion.div
                      className="z-20 flex w-full max-w-sm flex-col gap-[var(--spacing)] text-center"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{
                        opacity: showTitle ? 0 : 1,
                        y: showTitle ? 20 : 0,
                      }}
                      transition={{
                        duration: 0.7,
                        ease: [0.22, 1, 0.36, 1],
                        delay: showTitle ? 0 : 0.55,
                      }}
                    >
                      <p
                        className={classNames(
                          styles.UiText__body,
                          'w-full text-balance text-center',
                        )}
                      >
                        We trace five defining moments across five decades to
                        New York’s most beloved museum.
                      </p>
                      <button
                        type="button"
                        className={classNames(
                          styles.UiButton,
                          'mx-auto h-[calc(var(--spacing)*3)] px-8',
                        )}
                        onClick={() => splideRef.current?.splide?.go('>')}
                      >
                        Explore the timeline
                      </button>
                    </motion.div>
                  </>
                ) : (
                  <SlideSection onClick={() => navigate(item.id)} item={item} />
                )}
              </SplideSlide>
            ))}
          </SplideTrack>
        </Splide>
      </motion.div>
    </motion.div>
  );
});

// ----- Timeline ----- //

const TimelineMenuIndicator = ({
  progress,
  spacing,
}: {
  progress: MotionValue<number>;
  spacing: number;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setCount(Math.ceil(entry.contentRect.height / Math.round(spacing)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [spacing]);

  useMotionValueEvent(progress, 'change', (latest) => {
    if (count > 0) {
      const newIndex = Math.min(Math.floor(latest * count), count - 1);
      if (newIndex !== activeIndex) {
        setActiveIndex(newIndex);
      }
    }
  });

  return (
    <div
      ref={containerRef}
      aria-hidden
      className={classNames(styles.UiTimeline__ul__li__scroll)}
    >
      {Array.from({ length: count }, (_, i) => {
        const dist = Math.abs(i - activeIndex);
        return (
          <span
            key={i}
            data-distance={dist}
            className={classNames(styles.UiTimeline__ul__li__scroll__span)}
          />
        );
      })}
    </div>
  );
};

const TimelineMenu = forwardRef<
  HTMLDivElement,
  {
    activeStep: TimelineEventEnum;
    scrollProgress: MotionValue<number>;
    spacing: number;
    onClick: (value: TimelineEventEnum) => void;
    style?: CSSProperties;
  }
>(({ activeStep, onClick, scrollProgress, spacing, style }, ref) => {
  return (
    <div ref={ref} style={style} className={classNames(styles.UiTimeline)}>
      <ul className={classNames(styles.UiTimeline__ul)}>
        {TIMELINE_EVENT_SECTIONS.filter(
          (item) => item.id !== TimelineEventEnum.HERO,
        ).map((item) => (
          <motion.li
            key={item.id}
            animate={{ flexGrow: activeStep === item.id ? 1 : 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 32 }}
            className={classNames(styles.UiTimeline__ul__li)}
          >
            <button
              aria-label={`Go to ${item.year}`}
              onClick={() => onClick(item.id)}
              className={classNames(
                styles.UiText__lg,
                'transition duration-300 ease-out hover:text-black/50',
              )}
            >
              {item.year}
            </button>
            {activeStep === item.id ? (
              <TimelineMenuIndicator
                spacing={spacing}
                progress={scrollProgress}
              />
            ) : null}
          </motion.li>
        ))}
      </ul>
    </div>
  );
});

const MotionTimelineMenu = motion.create(TimelineMenu);

const TimelinePostIt = ({
  depth = 1,
  eagerLoading,
  filename,
  positionClassName,
}: {
  eagerLoading?: boolean;
  filename: string;
  depth?: number;
  positionClassName: string;
}) => {
  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX / window.innerWidth);
      mouseY.set(e.clientY / window.innerHeight);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY]);

  const rawX = useTransform(mouseX, [0, 1], [-24 * depth, 24 * depth]);
  const rawY = useTransform(mouseY, [0, 1], [-24 * depth, 24 * depth]);
  const x = useSpring(rawX, { stiffness: 55, damping: 18, mass: 1.2 });
  const y = useSpring(rawY, { stiffness: 55, damping: 18, mass: 1.2 });

  return (
    <div
      aria-hidden
      className={classNames(
        'pointer-events-none absolute z-10 block aspect-[1/1] w-[max(20vh)]',
        positionClassName,
      )}
    >
      <motion.span
        className={classNames('relative block h-full w-full')}
        style={{ x, y }}
      >
        <Image
          alt=""
          eagerLoading={eagerLoading}
          size={{
            sm: { ratio: '1/1', span: 4 },
            md: { ratio: '1/1', span: 4 },
            lg: { ratio: '1/1', span: 4 },
          }}
          ignoreAspectRatio
          src={filename}
        />
      </motion.span>
    </div>
  );
};

// ----- Lightbox ----- //

const LightboxContext = createContext<{
  open: (src: string, alt: string) => void;
} | null>(null);

const Lightbox = ({
  alt,
  onClose,
  src,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <motion.div
      className="fixed inset-0 z-90 bg-black/90"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      style={
        {
          '--spacing': `${DEFAULT_SPACING}px`,
        } as CSSProperties
      }
    >
      <motion.figure
        className="fixed inset-0 z-10 flex flex-col items-center justify-center"
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 24, opacity: 0 }}
        transition={{
          duration: 0.45,
          ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
        }}
        onClick={onClose}
      >
        <img
          src={src}
          alt={alt}
          className="max-h-full max-w-full object-contain"
        />
        {alt && (
          <figcaption
            className={classNames(
              styles.UiText__body,
              'absolute bottom-0 left-0 max-w-md text-balance bg-black p-[var(--spacing)] text-white',
            )}
          >
            {alt}
          </figcaption>
        )}
      </motion.figure>
      <button
        aria-label="Close image"
        className={classNames(
          'absolute right-[var(--spacing)] top-[var(--spacing)] z-10 size-[calc(var(--spacing)*2)] md:size-[calc(var(--spacing)*3)]',
          styles.UiButton,
        )}
        onClick={onClose}
      >
        <Icon name="ic_close" className={classNames(styles.UiButton__icon)} />
      </button>
    </motion.div>
  );
};

const TimelineImage = ({
  alt,
  className,
  eagerLoading,
  filename,
}: {
  alt: string;
  className: string;
  eagerLoading?: boolean;
  filename: string;
}) => {
  const lightbox = useContext(LightboxContext);

  return (
    <button
      className={classNames(
        styles.UiTimeline__grid__image,
        className,
        'cursor-pointer',
      )}
      onClick={() => lightbox?.open(filename, alt)}
      aria-label={alt || 'View image'}
    >
      <Image
        alt={alt}
        eagerLoading={eagerLoading}
        size={{
          sm: { ratio: 'intrinsic', span: 12 },
          md: { ratio: 'intrinsic', span: 4 },
          lg: { ratio: 'intrinsic', span: 4 },
        }}
        ignoreAspectRatio
        src={filename}
      />
    </button>
  );
};

const Timeline = ({
  children,
  progress,
}: PropsWithChildren & { progress?: MotionValue<number> }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [outerHeight, setOuterHeight] = useState('100dvh');

  const trackRef = useRef<HTMLDivElement>(null);

  const isMobile = useMediaQuery('sm');
  const isMobileRef = useRef(isMobile);
  isMobileRef.current = isMobile;

  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    const recalculate = () => {
      if (isMobileRef.current || !trackRef.current) return;
      const overflow = trackRef.current.scrollWidth - window.innerWidth;
      if (overflow <= 0) return;
      const extraVh = (overflow / window.innerWidth) * 100;
      setOuterHeight(`${100 + extraVh}dvh`);
    };

    const observer = new ResizeObserver(recalculate);
    if (trackRef.current) observer.observe(trackRef.current);
    window.addEventListener('resize', recalculate);
    recalculate();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', recalculate);
    };
  }, [children]);

  const { scrollYProgress } = useScroll({ target: containerRef });

  useMotionValueEvent(scrollYProgress, 'change', (latest) => {
    progress?.set(latest);
  });

  const x = useTransform(scrollYProgress, (p) => {
    if (shouldReduceMotion || isMobileRef.current || !trackRef.current)
      return '0px';
    const distance = trackRef.current.scrollWidth - window.innerWidth;
    return `${-p * distance}px`;
  });

  return (
    <div
      ref={containerRef}
      style={{
        height: isMobile ? 'auto' : outerHeight,
      }}
    >
      <section className="w-full overflow-x-hidden md:sticky md:top-0 md:h-dvh">
        <motion.div
          ref={trackRef}
          style={{
            height: isMobile ? undefined : '100%',
            x: isMobile ? undefined : x,
          }}
          className="flex w-full flex-col max-md:pt-[var(--spacing)] md:w-[max-content] md:flex-row md:pl-48"
        >
          {children}
        </motion.div>
      </section>
    </div>
  );
};

// ----- Page ----- //

type PageProps = {
  locale: string;
  locales: string[];
  ogImageUrl: string;
  preview: boolean;
  story: Franchises;
  backgroundImage: StoryblokImage;
};

export async function getStaticProps(
  context: GetStaticPropsContext,
): Promise<GetStaticPropsResult<PageProps>> {
  const translations = await pageTranslations(context.locale || 'en', []);

  const page: StoryResponse<Franchises> = await getStoryblokPage(
    'cdn/stories/insights/articles/moma-ps1-i-love-you',
    {
      locale: context.locale,
      preview: context.preview,
    },
  );

  if (!page) {
    return {
      notFound: true,
    };
  }

  const story = page.data?.story;
  const heroPicker = story.content.heroPicker[0] as HeroImage;
  const backgroundImage = heroPicker.image;

  const ogImageUrl = await imageOgStaticLoader({
    titleKey: story.content.title,
    imageSrc: backgroundImage.filename,
    imageWidth: 1200,
    imageHeight: 630,
    overlay: true,
  });

  return {
    props: {
      locale: context.locale || 'en',
      locales: context.locales || [],
      preview: Boolean(context.preview),
      ogImageUrl,
      story,
      backgroundImage,
      ...translations,
    },
  };
}

const Page = ({
  locale,
  ogImageUrl,
  preview,
  story,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  useTheme('light');

  const page = useStoryblok(story, preview, locale);

  const router = useRouter();
  const { asPath, query } = router;
  const fallbackCanonicalUrl = toCanonicalUrl({
    path: asPath,
    locale,
    hostname: process.env.NEXT_PUBLIC_HOSTNAME!,
  });

  useEffect(() => {
    document.documentElement.classList.add('no-scrollbar');

    return () => {
      document.documentElement.classList.remove('no-scrollbar');
    };
  }, []);

  // Lightbox
  const mainRef = useRef<HTMLElement>(null);
  const [lightboxImage, setLightboxImage] = useState<{
    src: string;
    alt: string;
  } | null>(null);
  const openLightbox = useCallback(
    (src: string, alt: string) => setLightboxImage({ src, alt }),
    [],
  );
  const closeLightbox = useCallback(() => setLightboxImage(null), []);

  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    if (lightboxImage) {
      main.setAttribute('inert', '');
      document.body.style.overflow = 'hidden';
    } else {
      main.removeAttribute('inert');
      document.body.style.overflow = '';
    }
    return () => {
      main.removeAttribute('inert');
      document.body.style.overflow = '';
    };
  }, [lightboxImage]);

  // Navigation
  const [activeStep, setActiveStep] = useState<TimelineEventEnum>(
    TimelineEventEnum.HERO,
  );
  const [direction, setDirection] = useState(0);
  const [lastDetailedSection, setLastDetailedSection] =
    useState<TimelineEventEnum | null>(null);

  useEffect(() => {
    const section = query.section as TimelineEventEnum;
    if (section && TIMELINE_EVENT_IDS.includes(section)) {
      setActiveStep(section);
      if (section === TimelineEventEnum.HERO) {
        const rest = { ...router.query };
        delete rest.section;
        router.replace({ pathname: router.pathname, query: rest }, undefined, {
          shallow: true,
        });
      } else {
        setLastDetailedSection(section);
      }
    }
  }, [query.section]);

  const navigate = (target: TimelineEventEnum) => {
    setDirection(
      TIMELINE_EVENT_IDS.indexOf(target) >
        TIMELINE_EVENT_IDS.indexOf(activeStep)
        ? 1
        : -1,
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
    scrollProgress.set(0);
    setActiveStep(target);

    if (target !== TimelineEventEnum.HERO) {
      setLastDetailedSection(target);
    }

    if (target === TimelineEventEnum.HERO) {
      const rest = { ...router.query };
      delete rest.section;
      router.push({ pathname: router.pathname, query: rest }, undefined, {
        shallow: true,
      });
    } else {
      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, section: target },
        },
        undefined,
        { shallow: true },
      );
    }
  };

  // Progress
  const scrollProgress = useMotionValue(0);
  const progressGradient = useTransform(
    scrollProgress,
    (p) =>
      `conic-gradient(rgb(var(--shade-orange-500)) 0deg ${
        p * 360
      }deg, transparent ${p * 360}deg 360deg)`,
  );

  // Next section
  const SECTIONS = TIMELINE_EVENT_SECTIONS.filter(
    ({ id }) => id !== TimelineEventEnum.HERO,
  ) as TimelineEventSectionType[];
  const nextSection =
    SECTIONS[
      (SECTIONS.findIndex(({ id }) => id === activeStep) + 1) % SECTIONS.length
    ];

  return (
    <LightboxContext.Provider value={{ open: openLightbox }}>
      <AnimatePresence>
        {lightboxImage && (
          <Lightbox
            key="lightbox"
            src={lightboxImage.src}
            alt={lightboxImage.alt}
            onClose={closeLightbox}
          />
        )}
      </AnimatePresence>
      <main
        ref={mainRef}
        className={classNames(styles.UiMain, styles.UiText__body)}
        style={
          {
            '--spacing': `${DEFAULT_SPACING}px`,
          } as CSSProperties
        }
      >
        <Head
          description={page.content.seo.description}
          theme="light"
          title={page.content.seo.title}
          urls={{ canonical: fallbackCanonicalUrl }}
        />

        <SocialMetadata
          description={page.content.seo.description}
          imageUrl={ogImageUrl}
          title={page.content.seo.title}
          type="article"
          url={fallbackCanonicalUrl}
        />

        <HideHubspot />

        <AnimatePresence custom={direction} mode="popLayout">
          {activeStep !== TimelineEventEnum.HERO ? (
            <MotionTimelineMenu
              key="sectionTimeline"
              animate="animate"
              custom={direction}
              spacing={DEFAULT_SPACING}
              exit="exit"
              initial="initial"
              variants={sectionAnimations}
              activeStep={activeStep}
              scrollProgress={scrollProgress}
              onClick={(s) => navigate(s)}
            />
          ) : null}

          {activeStep !== TimelineEventEnum.HERO ? (
            <motion.div
              key="sectionClose"
              animate="animate"
              custom={direction}
              exit="exit"
              initial="initial"
              variants={sectionAnimations}
              className="fixed right-[var(--spacing)] top-[var(--spacing)] z-50"
            >
              <div className="relative size-[calc(var(--spacing)*2)] md:size-[calc(var(--spacing)*3)]">
                <motion.div
                  aria-hidden
                  className="absolute -inset-1 rounded-full"
                  style={{ background: progressGradient }}
                />
                <button
                  aria-label="Go to menu"
                  onClick={() => navigate(TimelineEventEnum.HERO)}
                  className={classNames('absolute inset-0', styles.UiButton)}
                >
                  <Icon
                    name="ic_close"
                    className={classNames(
                      styles.UiButton__icon,
                      'max-md:hidden',
                    )}
                  />
                  <Icon
                    name="ic_menu"
                    className={classNames(styles.UiButton__icon, 'md:hidden')}
                  />
                </button>
              </div>
            </motion.div>
          ) : null}

          {activeStep === TimelineEventEnum.HERO ? (
            <Hero
              direction={direction}
              navigate={navigate}
              startSection={lastDetailedSection || undefined}
            />
          ) : null}

          {activeStep === TimelineEventEnum.SECTION_1 ? (
            <motion.div
              key={TimelineEventEnum.SECTION_1}
              animate="animate"
              custom={direction}
              exit="exit"
              initial="initial"
              variants={sectionAnimations}
            >
              <Timeline progress={scrollProgress}>
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div className="col-start-1 -col-end-1 flex flex-col justify-between md:row-start-1 md:row-end-9">
                    <div className={classNames(styles.UiTimeline__grid__title)}>
                      <span
                        className={classNames(styles.UiText__bod, 'md:sr-only')}
                      >
                        {TimelineEventEnum.SECTION_1}
                      </span>
                      <h2 className={classNames(styles.UiText__lg)}>
                        {SECTION_TITLE[TimelineEventEnum.SECTION_1]}
                      </h2>
                    </div>
                    <p
                      className={classNames(
                        styles.UiText__body,
                        styles.UiTimeline__grid__text,
                      )}
                    >
                      It's 1970s New York. Civil Rights and Gay Liberation
                      movements are in full force, punk is emerging, and the art
                      world is shifting into a new multi-disciplinary era.
                      Amidst this energy, curator Alanna Heiss secures access to
                      a derelict school in Queens and hands it over to 78
                      artists—no rules, no limits. The result is <i>Rooms</i>,
                      with art filling every classroom, closet, bathroom and
                      boiler room. The exhibition is a radical act of urban
                      reclamation, and marks PS1's arrival on its own terms.
                    </p>
                  </div>
                  <div className="col-start-3 col-end-6 flex w-full overflow-hidden md:col-start-1 md:-col-end-1 md:row-start-10 md:-row-end-1 md:justify-end">
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZRcAv22kU8EkgLTzarnH8uQr1"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZRcAv22kU8EkgLTzarnH8uQr1'
                        ]
                      }
                      className="aspect-[4/3] max-md:w-full md:aspect-[1/1] md:h-full"
                      eagerLoading
                    />
                  </div>
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div className="col-start-1 col-end-3 flex w-full overflow-hidden md:col-start-1 md:-col-end-1 md:row-start-7 md:row-end-10">
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZSVey3FE0FZitKUJMRdWvbWOs"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZSVey3FE0FZitKUJMRdWvbWOs'
                        ]
                      }
                      className="aspect-[1/1] max-md:w-full md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[0]}
                    depth={1.5}
                    positionClassName="left-1/2 top-1/2 -translate-x-1/2 -translate-y-full md:left-0 md:top-0 md:-translate-x-1/2 md:translate-y-1/2"
                  />
                  <TimelinePostIt
                    filename={POST_ITS[1]}
                    depth={0.7}
                    positionClassName={classNames(
                      'left-2/3 top-1/2 -translate-x-1/2 -translate-y-1/2',
                      'md:left-0 md:top-3/4 md:-translate-x-1/4 md:-translate-y-1/3',
                    )}
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div className="col-start-1 col-end-3 row-start-2 row-end-4 flex w-full overflow-hidden md:col-start-1 md:-col-end-1 md:row-start-3 md:row-end-9 md:justify-end">
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZSZDlJkKdrRYcLftYbc59283i"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZSZDlJkKdrRYcLftYbc59283i'
                        ]
                      }
                      className="aspect-[2/3] max-md:w-full md:h-full"
                    />
                  </div>
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div className="col-start-3 col-end-5 row-start-1 row-end-2 flex w-full overflow-hidden md:col-start-2 md:-col-end-1 md:row-start-1 md:row-end-3 md:-translate-x-full">
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZSzqNdO5GuBa5ZdKhsJMyY1HM"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZSzqNdO5GuBa5ZdKhsJMyY1HM'
                        ]
                      }
                      className="w-full max-md:aspect-[4/3] md:h-full"
                    />
                  </div>
                  <div className="col-start-4 -col-end-1 row-start-2 row-end-3 flex w-full overflow-hidden md:col-start-2 md:col-end-5 md:row-start-3 md:row-end-7">
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZT3ub69HljrYVeCS6rPZNDiOf"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZT3ub69HljrYVeCS6rPZNDiOf'
                        ]
                      }
                      className="w-full max-md:aspect-[1/1] md:h-full"
                    />
                  </div>
                  <div className="col-start-1 col-end-4 row-start-5 row-end-6 flex w-full overflow-hidden md:col-start-1 md:-col-end-1 md:row-start-9 md:-row-end-1">
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZT908pSm0WSUzpK9YtuQvIbtM"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZT908pSm0WSUzpK9YtuQvIbtM'
                        ]
                      }
                      className="aspect-[1/1] max-md:w-full md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[2]}
                    depth={2}
                    positionClassName="left-1/2 top-1/2 -translate-x-1/2 translate-y-1/3 md:bottom-0 md:right-0 md:-translate-y-1/2 md:translate-x-1/4"
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div className="col-start-1 col-end-4 flex w-full overflow-hidden max-md:col-start-3 md:-col-end-1 md:row-start-7 md:row-end-9">
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZTJ99hg4wJUkOl4qmYRoQj9wq"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZTJ99hg4wJUkOl4qmYRoQj9wq'
                        ]
                      }
                      className="aspect-[3/2] max-md:w-full md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[3]}
                    depth={1.2}
                    positionClassName="left-1/2 top-1/2 -translate-x-full -translate-y-1/4 md:right-0 md:top-0 md:translate-x-1/2 md:translate-y-full"
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div className="col-start-2 col-end-3 row-start-1 row-end-3 flex w-full overflow-hidden md:col-start-1 md:-col-end-1 md:row-start-1 md:row-end-5">
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZTN3upch4eR90U3ORQkrcGV7U"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZTN3upch4eR90U3ORQkrcGV7U'
                        ]
                      }
                      className="w-full md:h-full"
                    />
                  </div>
                  <div className="col-start-4 -col-end-1 row-start-2 row-end-4 flex w-full overflow-hidden md:col-start-1 md:-col-end-1 md:row-start-9 md:-row-end-2">
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZTYWsD530KG4uHIIzO0GPvZ5i"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZTYWsD530KG4uHIIzO0GPvZ5i'
                        ]
                      }
                      className="aspect-[3/4] max-md:w-full md:h-full"
                    />
                  </div>
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div className="col-start-2 col-end-5 flex w-full overflow-hidden md:col-start-1 md:-col-end-1 md:row-start-5 md:row-end-9">
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZTabKr77GcDm6j1KbbYsQeh4J"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZTabKr77GcDm6j1KbbYsQeh4J'
                        ]
                      }
                      className="aspect-[4/3] max-md:w-full md:h-full"
                    />
                  </div>
                </div>
                {/*  */}
                <div
                  className={classNames(
                    styles.UiTimeline__grid,
                    'w-auto max-md:pt-[calc(var(--spacing)*4)] md:pl-[calc(var(--spacing)*4)]',
                  )}
                >
                  <div className="col-span-full row-span-full flex w-full items-center justify-center overflow-hidden md:aspect-[1/1]">
                    <SlideSection
                      onClick={() => navigate(nextSection.id)}
                      item={nextSection}
                    />
                  </div>
                </div>
              </Timeline>
            </motion.div>
          ) : null}

          {activeStep === TimelineEventEnum.SECTION_2 ? (
            <motion.div
              key={TimelineEventEnum.SECTION_2}
              animate="animate"
              custom={direction}
              exit="exit"
              initial="initial"
              variants={sectionAnimations}
            >
              <Timeline progress={scrollProgress}>
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 -col-end-1 row-start-1 -row-end-1',
                    )}
                  >
                    <div className={classNames(styles.UiTimeline__grid__title)}>
                      <span
                        className={classNames(styles.UiText__bod, 'md:sr-only')}
                      >
                        {TimelineEventEnum.SECTION_2}
                      </span>
                      <h2 className={classNames(styles.UiText__lg)}>
                        {SECTION_TITLE[TimelineEventEnum.SECTION_2]}
                      </h2>
                    </div>
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'xs:col-start col-start-1 -col-end-1 row-start-2 row-end-3',
                      'xs:-col-end-1 xs:row-start-2 xs:row-end-3 xs:justify-end',
                      'md:col-start-1 md:-col-end-1 md:row-start-5 md:-row-end-1 md:justify-start',
                    )}
                  >
                    <p
                      className={classNames(
                        styles.UiText__body,
                        styles.UiTimeline__grid__text,
                      )}
                    >
                      Into a city on the cusp of Reagan's America, curator Diego
                      Cortez—founder of the iconic Mudd Club—fills MoMA PS1 with
                      work by 118 artists, writers and musicians. Robert
                      Mapplethorpe and William S. Burroughs show alongside Nan
                      Goldin, Keith Haring, and a 20-year-old Jean-Michel
                      Basquiat. <i>New York/New Wave</i> is a direct challenge
                      to every assumption about who qualifies as an artist.
                    </p>
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-4 -col-end-1 row-start-3 row-end-6',
                      'md:col-start-1 md:-col-end-1 md:row-start-11 md:-row-end-1 md:items-end',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZV11N9YfLEEWNdQcg5nSEHmAa"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZV11N9YfLEEWNdQcg5nSEHmAa'
                        ]
                      }
                      className="aspect-[3/4] max-md:w-full md:h-full"
                    />
                  </div>
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 col-end-4 row-start-2 row-end-3',
                      'md:col-start-1 md:-col-end-1 md:row-start-6 md:-row-end-1',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZV11WhVKnJMoFHAyzZRwIu4zs"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZV11WhVKnJMoFHAyzZRwIu4zs'
                        ]
                      }
                      className="aspect-[3/4] h-full max-md:w-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[4]}
                    depth={1.5}
                    positionClassName="md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-[125%] left-3/4 top-full -translate-x-1/2 -translate-y-full"
                  />
                  <TimelinePostIt
                    filename={POST_ITS[5]}
                    depth={0.7}
                    positionClassName="left-0 top-full -translate-x-1/3 translate-y-full md:left-1/2 md:top-1/3 md:-translate-x-full md:-translate-y-full"
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-2 col-end-4',
                      'md:col-start-1 md:-col-end-1 md:row-start-2 md:row-end-11',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZZ38RmG2SWY39LvR1ZkHw162u"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZZ38RmG2SWY39LvR1ZkHw162u'
                        ]
                      }
                      className="aspect-[1/2] max-md:w-full md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[6]}
                    depth={0.7}
                    positionClassName="left-1/2 max-md:top-1/2 md:bottom-0 md:left-0 -translate-y-1/3 translate-x-1/3 md:-translate-x-1/3 md:-translate-y-full"
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-4 -col-end-1 row-start-1 row-end-4',
                      'md:col-start-1 md:-col-end-1 md:row-start-1 md:row-end-5',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZVABeDSOUMQCiRRRKbw3pOXQB"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZVABeDSOUMQCiRRRKbw3pOXQB'
                        ]
                      }
                      className="aspect-[4/3] max-md:w-full md:h-full"
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 col-end-4 row-start-7',
                      'md:-col-start-1 md:-col-end-1 md:row-start-11 md:-row-end-1',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZVAJ5aCIZLVhiFrwLktCGxp2C"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZVAJ5aCIZLVhiFrwLktCGxp2C'
                        ]
                      }
                      className="w-full max-md:aspect-[3/2] md:h-full"
                    />
                  </div>
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-3 col-end-4',
                      'md:col-start-1 md:-col-end-1 md:row-start-9 md:row-end-11',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZVHfnqg0eTPh4Ytvhyn7xFytm"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZVHfnqg0eTPh4Ytvhyn7xFytm'
                        ]
                      }
                      className="aspect-[1/1] max-md:w-full md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[7]}
                    depth={2}
                    positionClassName="left-1/3 md:left-0 -translate-x-full translate-y-1/3 md:top-0 top-1/2 md:-translate-x-1/4 md:translate-y-full"
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-4 col-end-6',
                      'md:col-start-1 md:-col-end-1 md:row-start-6 md:row-end-9',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZVJg8nbp4gExSXsIIKEeHYrLb"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZVJg8nbp4gExSXsIIKEeHYrLb'
                        ]
                      }
                      className="aspect-[4/3] max-md:w-full md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[8]}
                    depth={1.2}
                    positionClassName="max-xs:hidden bottom-0 right-0 md:-translate-y-1/2 translate-y-full -translate-x-1/2 md:translate-x-1/4"
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-2 col-end-4 row-start-1 row-end-2',
                      'md:col-start-1 md:-col-end-1 md:row-start-3 md:row-end-6',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZVM0kjkPnC0qnJlyLQdhUfF8O"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZVM0kjkPnC0qnJlyLQdhUfF8O'
                        ]
                      }
                      className="aspect-[3/2] max-md:w-full md:h-full"
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-3 col-end-5 row-start-2 row-end-3',
                      'md:col-start-1 md:col-end-3 md:row-start-9 md:-row-end-3',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DZVNfd0PseZDXmzS8vlTLpxAEP"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DZVNfd0PseZDXmzS8vlTLpxAEP'
                        ]
                      }
                      className="aspect-[2/3] max-md:w-full md:h-full"
                    />
                  </div>
                </div>
                {/*  */}
                <div
                  className={classNames(
                    styles.UiTimeline__grid,
                    'w-auto max-md:pt-[calc(var(--spacing)*4)] md:pl-[calc(var(--spacing)*4)]',
                  )}
                >
                  <div className="col-span-full row-span-full flex w-full items-center justify-center overflow-hidden md:aspect-[1/1]">
                    <SlideSection
                      onClick={() => navigate(nextSection.id)}
                      item={nextSection}
                    />
                  </div>
                </div>
              </Timeline>
            </motion.div>
          ) : null}

          {activeStep === TimelineEventEnum.SECTION_3 ? (
            <motion.div
              key={TimelineEventEnum.SECTION_3}
              animate="animate"
              custom={direction}
              exit="exit"
              initial="initial"
              variants={sectionAnimations}
            >
              <Timeline progress={scrollProgress}>
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 -col-end-1 row-start-1 -row-end-1',
                      'md:col-start-1 md:-col-end-1 md:row-start-1 md:-row-end-8',
                    )}
                  >
                    <div className={classNames(styles.UiTimeline__grid__title)}>
                      <span
                        className={classNames(styles.UiText__bod, 'md:sr-only')}
                      >
                        {TimelineEventEnum.SECTION_3}
                      </span>
                      <h2 className={classNames(styles.UiText__lg)}>
                        {SECTION_TITLE[TimelineEventEnum.SECTION_3]}
                      </h2>
                    </div>
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 -col-end-1 row-start-3 row-end-5',
                      'xs:col-start-2 xs:-col-end-1 xs:row-start-3 xs:row-end-5',
                      'md:col-start-1 md:-col-end-1 md:row-start-8 md:-row-end-1',
                    )}
                  >
                    <p
                      className={classNames(
                        styles.UiText__body,
                        styles.UiTimeline__grid__text,
                      )}
                    >
                      After a three-year renovation, MoMA PS1 re-emerges more
                      than double its size. That summer, art collective Gelatin
                      transforms the courtyard into an urban beach—pools,
                      sprinklers, saunas, DJs—inaugurating <i>Warm Up</i>, the
                      concert series that becomes one of the museum's most
                      iconic programmes. At the turn of the millenium, PS1 and
                      MoMA officially merge.
                    </p>
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 -col-end-3 row-start-2 row-end-3',
                      'md:col-start-1 md:-col-end-1 md:row-start-4 md:row-end-8',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Da2LG0HFTM31fVtjZsneuYJaR3"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Da2LG0HFTM31fVtjZsneuYJaR3'
                        ]
                      }
                      className="w-full max-md:aspect-[1/1] md:h-full"
                      eagerLoading
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-3 col-end-5',
                      'md:col-start-1 md:-col-end-1 md:row-start-12 md:-row-end-1 md:items-end',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Da2FnAv6bjzR7sS3HGgK98urGc"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Da2FnAv6bjzR7sS3HGgK98urGc'
                        ]
                      }
                      className="aspect-[3/4] max-md:w-full md:h-full"
                      eagerLoading
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[9]}
                    depth={1.5}
                    positionClassName={classNames(
                      'left-1/2 top-1/2 -translate-y-[120%] translate-x-0',
                      'md:left-0 md:top-0 md:translate-x-[150%] md:translate-y-1/2',
                    )}
                  />
                  <TimelinePostIt
                    filename={POST_ITS[10]}
                    depth={2}
                    positionClassName={classNames(
                      'left-1/3 top-2/3 -translate-x-1/2 translate-y-2/3',
                      'md:left-full md:top-1/2 md:-translate-y-1/2 md:translate-x-full',
                    )}
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-5 -col-end-1 row-start-1 row-end-2',
                      'md:col-start-1 md:-col-end-1 md:row-start-1 md:row-end-4 md:items-end',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Da5oAE9vO9k9FwSl0Tmk1pV9cH"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Da5oAE9vO9k9FwSl0Tmk1pV9cH'
                        ]
                      }
                      className="aspect-[1/1] max-md:w-full md:h-full"
                      eagerLoading
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 col-end-3 row-start-1 row-end-3',
                      'md:col-start-1 md:-col-end-1 md:row-start-8 md:row-end-12',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Da2L9s7ZwcrhS5c5PhJarX56uI"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Da2L9s7ZwcrhS5c5PhJarX56uI'
                        ]
                      }
                      className="aspect-[4/3] max-md:w-full md:h-full"
                      eagerLoading
                    />
                  </div>
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 col-end-3',
                      'xs:row-start-2 xs:row-end-3',
                      'md:col-start-1 md:-col-end-1 md:row-start-4 md:row-end-7',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Da2E48A1EXyWGsATMYhLTTYMlx"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Da2E48A1EXyWGsATMYhLTTYMlx'
                        ]
                      }
                      className="aspect-[4/3] max-md:w-full md:h-full"
                    />
                  </div>
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-3 col-end-4 row-start-1 row-end-2',
                      'md:col-start-1 md:col-end-3 md:row-start-1 md:row-end-4',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Da2EfVETDrGcf34h4S96YalipR"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Da2EfVETDrGcf34h4S96YalipR'
                        ]
                      }
                      className="aspect-[3/2] max-md:w-full md:h-full"
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-2 col-end-4 row-start-3 row-end-4',
                      'md:col-start-1 md:col-end-2 md:row-start-7 md:row-end-10 md:-translate-x-1/2',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Da2FG1iAoOsAuwjV660jBJoUUy"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Da2FG1iAoOsAuwjV660jBJoUUy'
                        ]
                      }
                      className="aspect-[3/4] max-md:w-full md:h-full"
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-4 -col-end-1 row-start-2 row-end-3',
                      'md:col-start-1 md:-col-end-1 md:row-start-10 md:-row-end-1',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Da2LBfh8O8yNXUq7ZZHIpVBNaL"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Da2LBfh8O8yNXUq7ZZHIpVBNaL'
                        ]
                      }
                      className="aspect-[4/3] max-md:w-full md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[11]}
                    depth={0.7}
                    positionClassName={classNames(
                      'left-0 top-0 translate-x-1/4 translate-y-1/2',
                      'md:left-0 md:top-1/3 md:translate-x-full md:translate-y-full',
                    )}
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 col-end-3 row-start-1 row-end-3',
                      'md:col-start-1 md:-col-end-1 md:row-start-2 md:row-end-7',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Da2PNidxvTQwCtFfwmOCyPMbuP"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Da2PNidxvTQwCtFfwmOCyPMbuP'
                        ]
                      }
                      className="aspect-[1/1] max-md:w-full md:h-full"
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-4 -col-end-1 row-start-2 row-end-5',
                      'md:col-start-1 md:-col-end-1 md:row-start-12 md:-row-end-1',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Da2LIsTLh4vcTmpcCxM6ILcHJC"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Da2LIsTLh4vcTmpcCxM6ILcHJC'
                        ]
                      }
                      className="aspect-[4/3] max-md:w-full md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[12]}
                    depth={0.7}
                    positionClassName={classNames(
                      'left-1/2 top-1/2 -translate-y-full translate-x-1/2',
                      'md:left-0 md:top-0 md:-translate-x-1/2 md:translate-y-1/4',
                    )}
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-3 col-end-5',
                      'md:col-start-1 md:-col-end-1 md:row-start-7 md:row-end-12',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Da2POICTiehZkIs2QlAqC0eV4l"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Da2POICTiehZkIs2QlAqC0eV4l'
                        ]
                      }
                      className="aspect-[2/3] max-md:w-full md:h-full"
                    />
                  </div>
                </div>
                {/*  */}
                <div
                  className={classNames(
                    styles.UiTimeline__grid,
                    'w-auto max-md:pt-[calc(var(--spacing)*4)] md:pl-[calc(var(--spacing)*4)]',
                  )}
                >
                  <div className="col-span-full row-span-full flex w-full items-center justify-center overflow-hidden md:aspect-[1/1]">
                    <SlideSection
                      onClick={() => navigate(nextSection.id)}
                      item={nextSection}
                    />
                  </div>
                </div>
              </Timeline>
            </motion.div>
          ) : null}

          {activeStep === TimelineEventEnum.SECTION_4 ? (
            <motion.div
              key={TimelineEventEnum.SECTION_4}
              animate="animate"
              custom={direction}
              exit="exit"
              initial="initial"
              variants={sectionAnimations}
            >
              <Timeline progress={scrollProgress}>
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 -col-end-1 row-start-1 -row-end-1',
                    )}
                  >
                    <div className={classNames(styles.UiTimeline__grid__title)}>
                      <span
                        className={classNames(styles.UiText__bod, 'md:sr-only')}
                      >
                        {TimelineEventEnum.SECTION_4}
                      </span>
                      <h2 className={classNames(styles.UiText__lg)}>
                        {SECTION_TITLE[TimelineEventEnum.SECTION_4]}
                      </h2>
                    </div>
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-3 -col-end-1',
                      'md:col-start-1 md:-col-end-1 md:row-start-4 md:row-end-9 md:items-end',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DcfGKpxH6EtbQAEshAfx480Jg5"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DcfGKpxH6EtbQAEshAfx480Jg5'
                        ]
                      }
                      className="aspect-[3/4] max-md:w-full md:h-full"
                      eagerLoading
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 col-end-3 row-start-3 row-end-4',
                      'md:col-start-1 md:-col-end-1 md:row-start-12 md:-row-end-1 md:items-end',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DcelF7HgU0cqMKwhBi6ymPdJCs"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DcelF7HgU0cqMKwhBi6ymPdJCs'
                        ]
                      }
                      className="aspect-[3/4] max-md:w-full md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[13]}
                    depth={1.2}
                    positionClassName={classNames(
                      'left-1/2 top-1/2 translate-x-1/2 translate-y-1/2',
                      'md:left-1/2 md:top-3/4 md:-translate-x-1/2 md:-translate-y-1/2',
                    )}
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 -col-end-1 row-start-1 -row-end-1',
                      'xs:col-start-1 xs:col-end-4 xs:row-start-1 xs:row-end-3',
                      'md:col-start-1 md:-col-end-2 md:row-start-1 md:row-end-8 md:justify-end',
                    )}
                  >
                    <p
                      className={classNames(
                        styles.UiText__body,
                        styles.UiTimeline__grid__text,
                      )}
                    >
                      MoMA PS1 opens{' '}
                      <i>WACK! Art and the Feminist Revolution</i>—the first
                      major institutional survey of feminist art, defining a
                      generation of 120 international artists spanning 1965 to
                      1980. Curated by now-director Connie Butler, the show is
                      broad in scope and unambiguous in ambition,and remains a
                      landmark in the ongoing effort to expand whose work gets
                      shown, studied, and remembered.
                    </p>
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-4 -col-end-1 row-start-2 row-end-3',
                      'md:col-start-1 md:-col-end-1 md:row-start-8 md:row-end-12',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DcexjKJGmHrzTUS4U1Vz7Pq5hK"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DcexjKJGmHrzTUS4U1Vz7Pq5hK'
                        ]
                      }
                      className="w-full max-md:aspect-[4/3] md:h-full"
                    />
                  </div>
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 -col-end-2 row-start-2 row-end-3',
                      'md:col-start-1 md:-col-end-1 md:row-start-4 md:row-end-8',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Dcel83PWAx4vuUfENDD6Q0JoSf"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Dcel83PWAx4vuUfENDD6Q0JoSf'
                        ]
                      }
                      className="w-full max-md:aspect-[3/2] md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[14]}
                    depth={0.7}
                    positionClassName={classNames(
                      'left-0 top-0 -translate-y-full translate-x-1/4',
                      'md:left-0 md:top-0 md:-translate-x-1/2 md:translate-y-1/4',
                    )}
                  />
                  <TimelinePostIt
                    filename={POST_ITS[15]}
                    depth={2}
                    positionClassName={classNames(
                      'left-1/3 top-1/2 -translate-y-1/3 translate-x-full',
                      'md:left-3/4 md:top-3/4 md:-translate-x-1/3 md:-translate-y-1/4',
                    )}
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-4 -col-end-1 row-start-1 row-end-2',
                      'md:col-start-1 md:-col-end-2 md:row-start-1 md:row-end-5 md:items-start',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Dcf5w3PI1FZpljwoTY4d0pYI7V"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Dcf5w3PI1FZpljwoTY4d0pYI7V'
                        ]
                      }
                      className="aspect-[1/1] max-md:w-full md:h-full"
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 -col-end-3 row-start-3 row-end-4',
                      'md:col-start-1 md:-col-end-1 md:row-start-6 md:row-end-10',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DceS71ILVPpqsdGnVWIkf4gTQu"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DceS71ILVPpqsdGnVWIkf4gTQu'
                        ]
                      }
                      className="w-full max-md:aspect-[4/3] md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[16]}
                    depth={1.5}
                    positionClassName={classNames(
                      'left-1/3 top-0 -translate-x-1/2 translate-y-1/4',
                      'md:left-3/4 md:top-1/4 md:-translate-x-1/3 md:-translate-y-1/4',
                    )}
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-3 col-end-5 row-start-1 row-end-2',
                      'md:col-start-1 md:-col-end-1 md:row-start-1 md:row-end-8',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DceS2n134n7J3Xr8YB4BJXbRlY"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DceS2n134n7J3Xr8YB4BJXbRlY'
                        ]
                      }
                      className="aspect-[3/4] max-md:w-full md:h-full"
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-4 -col-end-1 row-start-3 row-end-4',
                      'md:col-start-1 md:-col-end-1 md:row-start-11 md:-row-end-1',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DcelAhhGV01UsgydAfMeusUO5P"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DcelAhhGV01UsgydAfMeusUO5P'
                        ]
                      }
                      className="w-full max-md:aspect-[4/3] md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[17]}
                    depth={0.5}
                    positionClassName={classNames(
                      'left-1/3 top-2/3 -translate-x-1/2 -translate-y-1/2',
                      'md:left-1/2 md:top-1/2 md:-translate-y-1/2 md:translate-x-1/2',
                    )}
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 col-end-3 row-start-1 row-end-2',
                      'md:col-start-1 md:-col-end-1 md:row-start-8 md:row-end-11',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DcexiiKobXV7CTilTpdXoLX9LO"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DcexiiKobXV7CTilTpdXoLX9LO'
                        ]
                      }
                      className="aspect-[3/2] max-md:w-full md:h-full"
                    />
                  </div>
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-3 col-end-5 row-start-1 row-end-2',
                      'md:col-start-1 md:-col-end-1 md:row-start-1 md:row-end-4 md:-translate-x-1/2',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DceS8XFRrK4VdLlqUzv7rRlRWb"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DceS8XFRrK4VdLlqUzv7rRlRWb'
                        ]
                      }
                      className="aspect-[4/3] max-md:w-full md:h-full"
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 col-end-4 row-start-2 row-end-3',
                      'md:col-start-1 md:-col-end-1 md:row-start-4 md:row-end-8',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DcexmTbejIcYozf0Vw4aFMnaQS"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DcexmTbejIcYozf0Vw4aFMnaQS'
                        ]
                      }
                      className="aspect-[1/1] max-md:w-full md:h-full"
                    />
                  </div>
                </div>
                {/*  */}
                <div
                  className={classNames(
                    styles.UiTimeline__grid,
                    'w-auto max-md:pt-[calc(var(--spacing)*4)] md:pl-[calc(var(--spacing)*4)]',
                  )}
                >
                  <div className="col-span-full row-span-full flex w-full items-center justify-center overflow-hidden md:aspect-[1/1]">
                    <SlideSection
                      onClick={() => navigate(nextSection.id)}
                      item={nextSection}
                    />
                  </div>
                </div>
              </Timeline>
            </motion.div>
          ) : null}

          {activeStep === TimelineEventEnum.SECTION_5 ? (
            <motion.div
              key={TimelineEventEnum.SECTION_5}
              animate="animate"
              custom={direction}
              exit="exit"
              initial="initial"
              variants={sectionAnimations}
            >
              <Timeline progress={scrollProgress}>
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 -col-end-1 row-start-1 -row-end-1',
                    )}
                  >
                    <div className={classNames(styles.UiTimeline__grid__title)}>
                      <span
                        className={classNames(styles.UiText__bod, 'md:sr-only')}
                      >
                        {TimelineEventEnum.SECTION_5}
                      </span>
                      <h2 className={classNames(styles.UiText__lg)}>
                        {SECTION_TITLE[TimelineEventEnum.SECTION_5]}
                      </h2>
                    </div>
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 -col-end-1 row-start-2',
                      'md:col-start-1 md:-col-end-1 md:row-start-4 md:-row-end-1',
                    )}
                  >
                    <p
                      className={classNames(
                        styles.UiText__body,
                        styles.UiTimeline__grid__text,
                      )}
                    >
                      50 years in, MoMA PS1 continues to reinvent what a museum
                      can be. <i>Greater New York</i>, now in its sixth edition,
                      takes over the full building just as <i>Rooms</i> did half
                      a century before, bringing together 53 artists across more
                      than 150 works. A reminder that PS1 has always belonged to
                      New York artists, and always will.
                    </p>
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-3 -col-end-1 row-start-3',
                      'md:col-start-1 md:-col-end-1 md:row-start-10 md:-row-end-1',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Dcg96MwWeypXNE4FxZd6giXLUe"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Dcg96MwWeypXNE4FxZd6giXLUe'
                        ]
                      }
                      className="aspect-[5/4] max-md:w-full md:h-full"
                      eagerLoading
                    />
                  </div>
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-2 col-end-3 row-start-1',
                      'md:col-start-1 md:col-end-2 md:row-start-3 md:row-end-6 md:-translate-x-1/3',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DcgPJXKJBgkh45jACzBMYTQCXf"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DcgPJXKJBgkh45jACzBMYTQCXf'
                        ]
                      }
                      className="aspect-[3/4] max-md:w-full md:h-full"
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-3 -col-end-2 row-start-2',
                      'md:col-start-1 md:-col-end-1 md:row-start-6 md:row-end-10',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DcgYLXWXTaO1IujPomINnf3bXL"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DcgYLXWXTaO1IujPomINnf3bXL'
                        ]
                      }
                      className="aspect-[3/2] max-md:w-full md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[18]}
                    depth={1.8}
                    positionClassName={classNames(
                      'left-3/4 top-full -translate-x-1/2 translate-y-[125%]',
                      'md:left-2/3 md:top-0 md:translate-x-1/2 md:translate-y-1/2',
                    )}
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 col-end-3 row-start-2 row-end-4',
                      'md:col-start-1 md:-col-end-1 md:row-start-1 md:row-end-7',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Dcftq1rhOcrRRbfD7W6spYMOTs"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Dcftq1rhOcrRRbfD7W6spYMOTs'
                        ]
                      }
                      className="aspect-[1/2] max-md:w-full md:h-full"
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-3 -col-end-2 row-start-4',
                      'md:col-start-1 md:-col-end-1 md:row-start-10 md:row-end-13',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DcgPOSt4mlq73rQE4jiUThNSza"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DcgPOSt4mlq73rQE4jiUThNSza'
                        ]
                      }
                      className="aspect-[3/2] max-md:w-full md:h-full"
                    />
                  </div>
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-2 -col-end-2 row-start-2 row-end-4',
                      'md:col-start-1 md:-col-end-1 md:row-start-2 md:row-end-6',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DcftnTVVOoJ7slLvBjewhY0k4q"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DcftnTVVOoJ7slLvBjewhY0k4q'
                        ]
                      }
                      className="aspect-[4/3] max-md:w-full md:h-full"
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-5 -col-end-1 row-start-1',
                      'md:col-start-1 md:col-end-2 md:row-start-13 md:-row-end-1',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DcgYYkELZwr1hyNCGAtqfDna8E"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DcgYYkELZwr1hyNCGAtqfDna8E'
                        ]
                      }
                      className="aspect-[5/4] max-md:w-full md:h-full"
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 col-end-2 row-start-3 row-end-6',
                      'md:col-start-2 md:-col-end-1 md:row-start-6 md:row-end-13',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DcgDemWMPaVhosuZ4Yoyu7oEBH"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DcgDemWMPaVhosuZ4Yoyu7oEBH'
                        ]
                      }
                      className="aspect-[1/2] max-md:w-full md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[19]}
                    depth={1.2}
                    positionClassName={classNames(
                      'left-1/2 top-1/2 -translate-x-[150%] -translate-y-[150%]',
                      'md:left-1/2 md:top-1/2 md:-translate-x-2/3 md:-translate-y-2/3',
                    )}
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 col-end-2 row-start-2',
                      'md:col-start-1 md:col-end-3 md:row-start-1 md:row-end-3',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Dcftpe1RWtPOvnupK8E0D9jDaj"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Dcftpe1RWtPOvnupK8E0D9jDaj'
                        ]
                      }
                      className="aspect-[4/3] max-md:w-full md:h-full"
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-3 -col-end-1 row-start-2 row-end-4',
                      'md:col-start-1 md:-col-end-1 md:row-start-6 md:row-end-10',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DcgYSCdcToPBXNmCUyn8WEcudI"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DcgYSCdcToPBXNmCUyn8WEcudI'
                        ]
                      }
                      className="aspect-[5/4] max-md:w-full md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[20]}
                    depth={0.7}
                    positionClassName={classNames(
                      'left-1/3 top-1/3 -translate-x-1/2 translate-y-full',
                      'md:left-0 md:top-1/2 md:-translate-x-1/2 md:translate-y-[120%]',
                    )}
                  />
                </div>
                {/*  */}
                <div className={classNames(styles.UiTimeline__grid, 'w-auto')}>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-1 col-end-3 row-start-1',
                      'md:col-start-1 md:col-end-2 md:row-start-4 md:row-end-6',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3Dcg94i1VoKhYZFtDXW8pfysXXt"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3Dcg94i1VoKhYZFtDXW8pfysXXt'
                        ]
                      }
                      className="aspect-[3/2] max-md:w-full md:h-full"
                    />
                  </div>
                  <div
                    className={classNames(
                      styles.UiTimeline__grid__item,
                      'col-start-3 -col-end-2 row-start-2',
                      'md:col-start-1 md:-col-end-1 md:row-start-10 md:-row-end-1',
                    )}
                  >
                    <TimelineImage
                      filename="https://images.avantarte.org/media/3DcgPGgXm59Axez0KC4fYxdqbFw"
                      alt={
                        IMAGES_ALT[
                          'https://images.avantarte.org/media/3DcgPGgXm59Axez0KC4fYxdqbFw'
                        ]
                      }
                      className="aspect-[4/3] max-md:w-full md:h-full"
                    />
                  </div>
                  <TimelinePostIt
                    filename={POST_ITS[21]}
                    depth={2}
                    positionClassName={classNames(
                      'left-1/3 top-1/2 -translate-x-1/3 translate-y-1/3 max-xs:hidden',
                      'md:left-3/4 md:top-1/2 md:translate-x-2/3 md:translate-y-1/3',
                    )}
                  />
                </div>
                {/*  */}
                <div
                  className={classNames(
                    styles.UiTimeline__grid,
                    'w-auto max-md:pt-[calc(var(--spacing)*4)] md:pl-[calc(var(--spacing)*4)]',
                  )}
                >
                  <div className="col-span-full row-span-full flex w-full items-center justify-center overflow-hidden md:aspect-[1/1]">
                    <SlideSection
                      onClick={() => navigate(nextSection.id)}
                      item={nextSection}
                    />
                  </div>
                </div>
              </Timeline>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </main>
    </LightboxContext.Provider>
  );
};

export default Page;
